import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, getAuthHeader } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Plus, Upload, Send, Trash2, UserCheck, Users, Briefcase, AlertTriangle, Edit2, Image, Award, FileDown, Camera, CalendarCheck, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TourDetail() {
  const { tourId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("students");
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", phone: "", email: "", aadhar: "", role: "Teacher", is_incharge: false });
  const [editingStaff, setEditingStaff] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [gallery, setGallery] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [certsGenerating, setCertsGenerating] = useState(false);
  const [selectedStudentForPhoto, setSelectedStudentForPhoto] = useState("");
  const [groupCaption, setGroupCaption] = useState("");
  const [galleryStudentFilter, setGalleryStudentFilter] = useState("");
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  useEffect(() => { load(); }, [tourId]);

  useEffect(() => {
    if (tab === "attendance" && !attendanceSummary && !attendanceLoading) {
      loadAttendanceSummary();
    }
  }, [tab]);

  const loadAttendanceSummary = async () => {
    setAttendanceLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/portal/tours/${tourId}/attendance/summary`, { headers: getAuthHeader() });
      setAttendanceSummary(data);
    } catch { toast.error("Failed to load attendance summary"); }
    finally { setAttendanceLoading(false); }
  };

  const load = async () => {
    const h = { headers: getAuthHeader() };
    const [t, s, st] = await Promise.all([
      axios.get(`${API}/api/portal/tours/${tourId}`, h),
      axios.get(`${API}/api/portal/tours/${tourId}/students`, h),
      axios.get(`${API}/api/portal/tours/${tourId}/staff`, h),
    ]);
    setTour(t.data);
    setStudents(s.data);
    setStaff(st.data);
    if (user?.role === "super_admin") {
      try {
        const [g, c] = await Promise.all([
          axios.get(`${API}/api/admin/tours/${tourId}/gallery`, h),
          axios.get(`${API}/api/admin/tours/${tourId}/certificates`, h),
        ]);
        setGallery(g.data);
        setCertificates(c.data);
      } catch {}
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/portal/tours/${tourId}/students/bulk`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} students!`);
      if (data.errors.length) toast.error(`${data.errors.length} errors during import`);
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleSubmitTour = async () => {
    if (students.length === 0) { toast.error("Add students before submitting"); return; }
    if (!window.confirm("Submit this tour to OSAA? You can still edit after.")) return;
    try {
      await axios.post(`${API}/api/portal/tours/${tourId}/submit`, {}, { headers: getAuthHeader() });
      toast.success("Tour submitted to OSAA!");
      load();
    } catch { toast.error("Failed to submit"); }
  };

  const saveStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name) { toast.error("Name is required"); return; }
    try {
      if (editingStaff) {
        await axios.put(`${API}/api/portal/tours/${tourId}/staff/${editingStaff}`, staffForm, { headers: getAuthHeader() });
        toast.success("Staff updated");
      } else {
        await axios.post(`${API}/api/portal/tours/${tourId}/staff`, staffForm, { headers: getAuthHeader() });
        toast.success("Staff added");
      }
      setShowStaffForm(false);
      setEditingStaff(null);
      setStaffForm({ name: "", phone: "", email: "", aadhar: "", role: "Teacher", is_incharge: false });
      load();
    } catch { toast.error("Failed to save staff"); }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    await axios.delete(`${API}/api/portal/tours/${tourId}/staff/${id}`, { headers: getAuthHeader() });
    toast.success("Deleted"); load();
  };

  const deleteStudent = async (id) => {
    if (!window.confirm("Delete this student?")) return;
    await axios.delete(`${API}/api/portal/tours/${tourId}/students/${id}`, { headers: getAuthHeader() });
    toast.success("Deleted"); load();
  };

  const classes = [...new Set(students.map(s => s.class_name).filter(Boolean))].sort();
  const filtered = students.filter(s => {
    if (search && !s.full_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (classFilter && s.class_name !== classFilter) return false;
    return true;
  });

  const flagged = students.filter(s => s.medical_conditions || s.allergies);
  const incharge = staff.find(s => s.is_incharge);

  const handleGalleryUpload = async (e, photoType, studentId = "", caption = "") => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("photo_type", photoType);
        fd.append("student_id", studentId || "");
        fd.append("caption", caption || "");
        await axios.post(`${API}/api/admin/tours/${tourId}/gallery`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded!`);
      load();
    } catch { toast.error("Upload failed"); }
    finally { setGalleryUploading(false); e.target.value = ""; }
  };

  const deleteGalleryPhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    await axios.delete(`${API}/api/admin/tours/${tourId}/gallery/${photoId}`, { headers: getAuthHeader() });
    toast.success("Deleted"); load();
  };

  const generateCertificates = async () => {
    if (!window.confirm("Generate certificates for all students?")) return;
    setCertsGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/tours/${tourId}/certificates`, {}, { headers: getAuthHeader() });
      toast.success(data.message);
      load();
    } catch { toast.error("Failed to generate certificates"); }
    finally { setCertsGenerating(false); }
  };

  const downloadCert = (certId) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/api/admin/certificates/${certId}/pdf?token=${token}`, "_blank");
  };

  const downloadAllCertsZip = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/api/admin/tours/${tourId}/certificates/zip?token=${token}`, "_blank");
  };

  if (!tour) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="tour-detail">
      {/* Header */}
      <header className="bg-primary text-primary-foreground px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/70 hover:text-primary-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Link>
            <Link
              to={`/portal/tours/${tourId}/teacher`}
              data-testid="teacher-mode-btn"
              className="inline-flex items-center gap-1.5 text-xs text-primary-foreground/90 border border-primary-foreground/30 px-2.5 py-1 rounded-sm hover:bg-primary-foreground/10"
            >
              📱 Teacher Mode
            </Link>
          </div>
          <h1 className="font-heading text-xl font-medium">{tour.name}</h1>
          <p className="text-sm text-primary-foreground/70">{tour.program_type} &middot; {tour.visit_date || "Date TBD"}</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
            <div className="font-heading text-2xl font-semibold">{students.length}</div>
            <div className="text-xs text-muted-foreground">Students</div>
          </div>
          <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
            <div className="font-heading text-2xl font-semibold">{staff.length}</div>
            <div className="text-xs text-muted-foreground">Staff</div>
          </div>
          <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
            <div className="font-heading text-2xl font-semibold">{incharge?.name || "-"}</div>
            <div className="text-xs text-muted-foreground">In-Charge</div>
          </div>
          <div className={`border p-4 rounded-sm text-center ${flagged.length > 0 ? 'bg-red-50 border-red-200' : 'bg-card border-border/60'}`}>
            <div className="font-heading text-2xl font-semibold flex items-center justify-center gap-1">
              {flagged.length > 0 && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {flagged.length}
            </div>
            <div className="text-xs text-muted-foreground">Medical Flags</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border/60 overflow-x-auto">
          {["students", "staff", "attendance", ...(user?.role === "super_admin" ? ["gallery", "certificates"] : []), "review"].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-5 py-2.5 text-sm font-medium capitalize transition-colors whitespace-nowrap ${tab === t ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Students Tab */}
        {tab === "students" && (
          <div>
            <div className="flex flex-wrap gap-3 mb-4">
              <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
              {classes.length > 0 && (
                <select value={classFilter} onChange={e => setClassFilter(e.target.value)} className="h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option value="">All Classes</option>
                  {classes.map(c => <option key={c} value={c}>Class {c}</option>)}
                </select>
              )}
              <div className="flex-1" />
              <label className={`inline-flex items-center gap-2 px-4 py-2 border border-border/60 text-sm rounded-sm cursor-pointer hover:bg-muted ${uploading ? 'opacity-50' : ''}`}>
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Bulk Upload"}
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleBulkUpload} className="hidden" disabled={uploading} />
              </label>
              <Link to={`/portal/tours/${tourId}/students/new`} data-testid="add-student-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground text-sm rounded-sm hover:bg-accent/90">
                <Plus className="w-4 h-4" /> Add Student
              </Link>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
                <Users className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No students yet. Add individually or use bulk upload (CSV/Excel).</p>
              </div>
            ) : (
              <div className="bg-card border border-border/60 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Photo</th>
                        <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Name</th>
                        <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Class</th>
                        <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Gender</th>
                        <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Medical</th>
                        <th className="text-right px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(s => (
                        <tr key={s.id} className="border-t border-border/40 hover:bg-muted/20">
                          <td className="px-4 py-3">
                            <div className="w-9 h-9 rounded-full bg-secondary overflow-hidden">
                              {s.photo_url ? <img src={s.photo_url.startsWith('/') ? `${API}${s.photo_url}` : s.photo_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-muted-foreground m-auto mt-2.5" />}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium">{s.full_name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{s.class_name}{s.section ? `-${s.section}` : ""}</td>
                          <td className="px-4 py-3 text-muted-foreground">{s.gender}</td>
                          <td className="px-4 py-3">
                            {(s.medical_conditions || s.allergies) ? (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                <AlertTriangle className="w-3 h-3" /> Flag
                              </span>
                            ) : <span className="text-xs text-green-600">Clear</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link to={`/portal/tours/${tourId}/students/${s.id}`} className="text-primary hover:text-accent mr-3"><Edit2 className="w-4 h-4 inline" /></Link>
                            <button onClick={() => deleteStudent(s.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Staff Tab */}
        {tab === "staff" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setShowStaffForm(true); setEditingStaff(null); setStaffForm({ name: "", phone: "", email: "", aadhar: "", role: "Teacher", is_incharge: false }); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground text-sm rounded-sm hover:bg-accent/90">
                <Plus className="w-4 h-4" /> Add Staff
              </button>
            </div>

            {showStaffForm && (
              <div className="bg-card border border-border/60 p-6 rounded-sm mb-6">
                <h3 className="font-heading text-lg font-medium mb-4">{editingStaff ? "Edit" : "Add"} Staff Member</h3>
                <form onSubmit={saveStaff} className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-xs font-medium mb-1 block">Full Name *</label><Input value={staffForm.name} onChange={e => setStaffForm(p => ({...p, name: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium mb-1 block">Phone</label><Input type="tel" value={staffForm.phone} onChange={e => setStaffForm(p => ({...p, phone: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium mb-1 block">Email</label><Input type="email" value={staffForm.email} onChange={e => setStaffForm(p => ({...p, email: e.target.value}))} /></div>
                  <div><label className="text-xs font-medium mb-1 block">Aadhaar Card</label><Input value={staffForm.aadhar} onChange={e => setStaffForm(p => ({...p, aadhar: e.target.value}))} /></div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Role</label>
                    <select value={staffForm.role} onChange={e => setStaffForm(p => ({...p, role: e.target.value}))} className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm">
                      <option>Teacher</option><option>Tour In-Charge</option><option>Coordinator</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={staffForm.is_incharge} onChange={e => setStaffForm(p => ({...p, is_incharge: e.target.checked}))} className="rounded" />
                      <UserCheck className="w-4 h-4 text-accent" /> Mark as Main In-Charge
                    </label>
                  </div>
                  <div className="sm:col-span-2 flex gap-3">
                    <button type="submit" className="px-6 py-2 bg-primary text-primary-foreground text-sm rounded-sm">Save</button>
                    <button type="button" onClick={() => { setShowStaffForm(false); setEditingStaff(null); }} className="px-6 py-2 border border-border/60 text-sm rounded-sm">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {staff.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
                <Briefcase className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No staff added yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {staff.map(s => (
                  <div key={s.id} className="flex items-center gap-4 bg-card border border-border/60 p-4 rounded-sm">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {s.is_incharge ? <UserCheck className="w-5 h-5 text-accent" /> : <Briefcase className="w-5 h-5 text-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{s.name}</span>
                        {s.is_incharge && <span className="text-[10px] bg-accent/20 text-accent px-2 py-0.5 rounded-full uppercase font-medium">Main In-Charge</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{s.role} &middot; {s.phone || "No phone"} &middot; {s.email || "No email"}</p>
                    </div>
                    <button onClick={() => { setEditingStaff(s.id); setStaffForm(s); setShowStaffForm(true); }} className="text-primary hover:text-accent"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteStaff(s.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Attendance Summary Tab */}
        {tab === "attendance" && (
          <div data-testid="attendance-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-medium flex items-center gap-2">
                  <CalendarCheck className="w-5 h-5 text-accent" /> Attendance Summary
                </h3>
                <p className="text-xs text-muted-foreground">
                  Attendance is captured day-by-day from Teacher Mode. This view shows the aggregate for this tour.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/portal/tours/${tourId}/teacher`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border/60 text-xs rounded-sm hover:bg-muted"
                  data-testid="attendance-mark-link"
                >
                  <Plus className="w-3.5 h-3.5" /> Mark attendance
                </Link>
                <button
                  onClick={() => {
                    const t = localStorage.getItem("token") || "";
                    window.location.href = `${API}/api/portal/tours/${tourId}/attendance/csv?token=${encodeURIComponent(t)}`;
                  }}
                  data-testid="attendance-export-csv-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded-sm hover:opacity-90"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </div>

            {attendanceLoading && <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>}

            {attendanceSummary && !attendanceLoading && (
              <>
                {/* Totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
                    <div className="font-heading text-2xl font-semibold">{attendanceSummary.dates.length}</div>
                    <div className="text-xs text-muted-foreground">Days Marked</div>
                  </div>
                  <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
                    <div className="font-heading text-2xl font-semibold text-green-600">{attendanceSummary.totals.present}</div>
                    <div className="text-xs text-muted-foreground">Total Present</div>
                  </div>
                  <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
                    <div className="font-heading text-2xl font-semibold text-amber-600">{attendanceSummary.totals.late}</div>
                    <div className="text-xs text-muted-foreground">Total Late</div>
                  </div>
                  <div className="bg-card border border-border/60 p-4 rounded-sm text-center">
                    <div className="font-heading text-2xl font-semibold text-red-600">{attendanceSummary.totals.absent}</div>
                    <div className="text-xs text-muted-foreground">Total Absent</div>
                  </div>
                </div>

                {attendanceSummary.dates.length === 0 ? (
                  <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
                    <CalendarCheck className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-muted-foreground">No attendance has been marked yet.</p>
                    <p className="text-xs text-muted-foreground mt-1">Open <b>Teacher Mode</b> to start capturing attendance.</p>
                  </div>
                ) : (
                  <>
                    {/* Per-date breakdown */}
                    <div className="bg-card border border-border/60 rounded-sm mb-6 overflow-hidden">
                      <div className="px-4 py-3 border-b border-border/60">
                        <h4 className="font-medium text-sm">By Date</h4>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium">Date</th>
                            <th className="text-right px-4 py-2 font-medium">Present</th>
                            <th className="text-right px-4 py-2 font-medium">Late</th>
                            <th className="text-right px-4 py-2 font-medium">Absent</th>
                            <th className="text-right px-4 py-2 font-medium">Unmarked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendanceSummary.by_date.map(d => {
                            const marked = d.present + d.late + d.absent;
                            const unmarked = attendanceSummary.total_students - marked;
                            return (
                              <tr key={d.date} className="border-t border-border/60">
                                <td className="px-4 py-2">{d.date}</td>
                                <td className="px-4 py-2 text-right text-green-600 font-medium">{d.present}</td>
                                <td className="px-4 py-2 text-right text-amber-600 font-medium">{d.late}</td>
                                <td className="px-4 py-2 text-right text-red-600 font-medium">{d.absent}</td>
                                <td className="px-4 py-2 text-right text-muted-foreground">{unmarked < 0 ? 0 : unmarked}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Per-student breakdown */}
                    <div className="bg-card border border-border/60 rounded-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-border/60">
                        <h4 className="font-medium text-sm">By Student</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium">Student</th>
                              <th className="text-left px-4 py-2 font-medium">Class</th>
                              <th className="text-right px-4 py-2 font-medium">Present</th>
                              <th className="text-right px-4 py-2 font-medium">Late</th>
                              <th className="text-right px-4 py-2 font-medium">Absent</th>
                              <th className="text-right px-4 py-2 font-medium">% Present</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attendanceSummary.by_student.map(s => {
                              const marked = s.present + s.late + s.absent;
                              const pct = marked > 0 ? Math.round((s.present / marked) * 100) : 0;
                              return (
                                <tr key={s.student_id} className="border-t border-border/60" data-testid={`attendance-student-${s.student_id}`}>
                                  <td className="px-4 py-2 font-medium">{s.full_name}</td>
                                  <td className="px-4 py-2 text-muted-foreground">{s.class_name}{s.section ? `-${s.section}` : ""}</td>
                                  <td className="px-4 py-2 text-right text-green-600">{s.present}</td>
                                  <td className="px-4 py-2 text-right text-amber-600">{s.late}</td>
                                  <td className="px-4 py-2 text-right text-red-600">{s.absent}</td>
                                  <td className="px-4 py-2 text-right">
                                    {marked > 0 ? (
                                      <span className={`font-medium ${pct >= 80 ? "text-green-600" : pct >= 50 ? "text-amber-600" : "text-red-600"}`}>{pct}%</span>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Gallery Tab (Super Admin) */}
        {tab === "gallery" && user?.role === "super_admin" && (
          <div data-testid="gallery-tab">
            {/* Upload Controls */}
            <div className="bg-card border border-border/60 rounded-sm p-4 mb-6 space-y-4">
              <h4 className="font-heading text-sm font-medium">Upload Photos</h4>

              {/* Individual (per-student) */}
              <div className="border border-border/60 rounded-sm p-3 bg-background">
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Individual (tag to student)</label>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={selectedStudentForPhoto}
                    onChange={(e) => setSelectedStudentForPhoto(e.target.value)}
                    data-testid="gallery-student-select"
                    className="flex-1 min-w-[200px] text-sm px-3 py-2 border border-border/60 rounded-sm bg-background"
                  >
                    <option value="">Select student...</option>
                    {students.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.full_name}{s.class_name ? ` (${s.class_name}${s.section ? "-" + s.section : ""})` : ""}
                      </option>
                    ))}
                  </select>
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm cursor-pointer ${(!selectedStudentForPhoto || galleryUploading) ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
                    data-testid="gallery-upload-individual"
                  >
                    <Camera className="w-4 h-4" /> Upload
                    <input
                      type="file" accept="image/*" multiple
                      onChange={(e) => { handleGalleryUpload(e, "individual", selectedStudentForPhoto); setSelectedStudentForPhoto(""); }}
                      className="hidden" disabled={galleryUploading || !selectedStudentForPhoto}
                    />
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Select a student, then upload one or more photos that will be tagged to them.</p>
              </div>

              {/* Group */}
              <div className="border border-border/60 rounded-sm p-3 bg-background">
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Group (optional caption)</label>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="text" placeholder="e.g. Class 7A, Day 2 Group"
                    value={groupCaption}
                    onChange={(e) => setGroupCaption(e.target.value)}
                    data-testid="gallery-group-caption"
                    className="flex-1 min-w-[200px] text-sm px-3 py-2 border border-border/60 rounded-sm bg-background"
                  />
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm cursor-pointer ${galleryUploading ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
                    data-testid="gallery-upload-group"
                  >
                    <Camera className="w-4 h-4" /> Upload
                    <input
                      type="file" accept="image/*" multiple
                      onChange={(e) => { handleGalleryUpload(e, "group", "", groupCaption); setGroupCaption(""); }}
                      className="hidden" disabled={galleryUploading}
                    />
                  </label>
                </div>
              </div>

              {/* Common */}
              <div className="border border-border/60 rounded-sm p-3 bg-background">
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Common (shared with all)</label>
                <label
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm cursor-pointer ${galleryUploading ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
                  data-testid="gallery-upload-common"
                >
                  <Camera className="w-4 h-4" /> Upload
                  <input
                    type="file" accept="image/*" multiple
                    onChange={(e) => handleGalleryUpload(e, "common")}
                    className="hidden" disabled={galleryUploading}
                  />
                </label>
              </div>
            </div>

            {/* Filter */}
            {gallery.length > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Filter by student:</label>
                <select
                  value={galleryStudentFilter}
                  onChange={(e) => setGalleryStudentFilter(e.target.value)}
                  data-testid="gallery-filter-student"
                  className="text-sm px-3 py-1.5 border border-border/60 rounded-sm bg-background"
                >
                  <option value="">All photos</option>
                  <option value="__none__">No tag (group/common)</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Individual photos - grouped by student */}
            {(() => {
              const individuals = gallery.filter(p => p.photo_type === "individual");
              const byStudent = individuals.reduce((acc, p) => {
                (acc[p.student_id] = acc[p.student_id] || []).push(p);
                return acc;
              }, {});
              const studentIds = Object.keys(byStudent).filter(sid =>
                !galleryStudentFilter || galleryStudentFilter === sid
              );
              if (galleryStudentFilter === "__none__" || studentIds.length === 0) return null;
              return (
                <div className="mb-6">
                  <h4 className="font-medium text-sm uppercase tracking-wider text-muted-foreground mb-3">Individual Photos</h4>
                  <div className="space-y-4">
                    {studentIds.map(sid => {
                      const student = students.find(s => s.id === sid);
                      const photos = byStudent[sid];
                      return (
                        <div key={sid} className="bg-card border border-border/60 rounded-sm p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-sm">{student?.full_name || "Unknown student"}</span>
                            <span className="text-xs text-muted-foreground">({photos.length} photo{photos.length > 1 ? 's' : ''})</span>
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {photos.map(p => (
                              <div key={p.id} className="relative group aspect-square rounded-sm overflow-hidden border border-border/60">
                                <img src={`${API}${p.url}`} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => deleteGalleryPhoto(p.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Group & Common photos */}
            {["group", "common"].map(type => {
              if (galleryStudentFilter && galleryStudentFilter !== "__none__") return null;
              const photos = gallery.filter(p => p.photo_type === type);
              if (photos.length === 0) return null;
              return (
                <div key={type} className="mb-6">
                  <h4 className="font-medium text-sm uppercase tracking-wider text-muted-foreground mb-3">{type} Photos ({photos.length})</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {photos.map(p => (
                      <div key={p.id} className="relative group aspect-square rounded-sm overflow-hidden border border-border/60">
                        <img src={`${API}${p.url}`} alt="" className="w-full h-full object-cover" />
                        {p.caption && <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">{p.caption}</div>}
                        <button onClick={() => deleteGalleryPhoto(p.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {gallery.length === 0 && (
              <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
                <Image className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No gallery photos uploaded yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Certificates Tab (Super Admin) */}
        {tab === "certificates" && user?.role === "super_admin" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">{certificates.length} certificates issued</p>
              <button onClick={generateCertificates} disabled={certsGenerating || students.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground text-sm rounded-sm hover:bg-accent/90 disabled:opacity-50">
                <Award className="w-4 h-4" /> {certsGenerating ? "Generating..." : "Generate All Certificates"}
              </button>
              {certificates.length > 0 && (
                <button onClick={downloadAllCertsZip} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm hover:bg-primary/90">
                  <FileDown className="w-4 h-4" /> Download All (ZIP)
                </button>
              )}
            </div>
            {certificates.length > 0 ? (
              <div className="bg-card border border-border/60 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Student</th>
                      <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Class</th>
                      <th className="text-left px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Cert ID</th>
                      <th className="text-right px-4 py-3 font-medium text-xs uppercase text-muted-foreground">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map(c => (
                      <tr key={c.id} className="border-t border-border/40">
                        <td className="px-4 py-3 font-medium">{c.student_name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{c.class_name}-{c.section}</td>
                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{c.cert_id}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => downloadCert(c.id)} className="inline-flex items-center gap-1 text-xs text-primary hover:text-accent">
                            <FileDown className="w-3.5 h-3.5" /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border/60 rounded-sm">
                <Award className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No certificates generated yet. Click "Generate All" to create them.</p>
              </div>
            )}
          </div>
        )}

        {/* Review & Submit Tab */}
        {tab === "review" && (
          <div className="max-w-lg mx-auto text-center py-8">
            <h3 className="font-heading text-2xl font-medium mb-6">Review & Submit</h3>
            <div className="bg-card border border-border/60 p-6 rounded-sm mb-6 text-left space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Tour:</span><span className="font-medium">{tour.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Program:</span><span className="font-medium">{tour.program_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Students:</span><span className="font-medium">{students.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Staff:</span><span className="font-medium">{staff.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">In-Charge:</span><span className="font-medium">{incharge?.name || "Not assigned"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><span className={`font-medium capitalize ${tour.status === 'submitted' ? 'text-blue-600' : 'text-yellow-600'}`}>{tour.status}</span></div>
              {flagged.length > 0 && (
                <div className="flex justify-between text-red-600"><span>Medical Flags:</span><span className="font-medium">{flagged.length} students</span></div>
              )}
            </div>
            {tour.status === "submitted" ? (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm text-blue-700 text-sm">Tour has been submitted to OSAA. You can still edit data.</div>
            ) : (
              <button onClick={handleSubmitTour} data-testid="submit-tour-btn"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground font-medium text-sm rounded-sm hover:bg-primary/90 transition-all">
                <Send className="w-4 h-4" /> Submit to Tour Provider
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
